import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { UserPermissionSearchFormValues } from "services/api/users/types";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { CheckBox } from "ui/components/CheckBox";
import Dropdown from "ui/components/Dropdown/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import MultiDropdown from "ui/components/Dropdown/MultiDropdown/MultiDropdown";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import { UserPermissionSearchFilterProps } from "./types";
import { initialUserPermissionSearchForm } from "services/api/users/consts";
import { useTranslate } from "helpers";
import { useHandleDropdownChange } from "services/forms/hooks";

const UserPermissionSearchFilters: React.FC<UserPermissionSearchFilterProps> = (
  props
) => {
  const { onSearchChange, onBulkEdit, selectedCount = 0, categories = [] } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<UserPermissionSearchFormValues>(
    initialUserPermissionSearchForm
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  useEffect(() => {
    onSearchChange(formValues);
  }, [formValues.assignmentFilter]);

  const assignmentFilterOptions: DropdownOption[] = useMemo(() => [
    { value: "assigned", text: t("admin.user.permission.search.filter.assigned") },
    { value: "unassigned", text: t("admin.user.permission.search.filter.unassigned") },
  ], [t]);

  const categoryOptions: MultiDropdownOption[] = useMemo(() => {
    return categories.map(cat => ({
      value: cat.id,
      text: cat.breadcrumb,
    }));
  }, [categories]);

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const updatedFormValues = { ...formValues, search: e.target.value };
    setFormValues(updatedFormValues);
    onSearchChange(updatedFormValues);
  };

  const handleCategoryChange = useCallback(
    (_name: string, selectedOptions: MultiDropdownOption[]) => {
      const categoryIds = selectedOptions.map(opt => opt.value);
      const updatedFormValues = { ...formValues, categoryIds };
      setFormValues(updatedFormValues);
      onSearchChange(updatedFormValues);
    },
    [formValues, onSearchChange]
  );

  const handleIncludeSubcategoriesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedFormValues = { ...formValues, includeSubcategories: e.target.checked };
      setFormValues(updatedFormValues);
      onSearchChange(updatedFormValues);
    },
    [formValues, onSearchChange]
  );

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialUserPermissionSearchForm);
    onSearchChange(initialUserPermissionSearchForm);
  }, [onSearchChange]);

  return (
    <>
      <div className=" ">
        <div className="flex justify-between items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="w-48">
              <TextField
                type="text"
                name="search"
                value={formValues.search}
                onChange={handleSearchChange}
                placeholder={t("admin.search.placeholder")}
                startIcon="../media/icons/search.svg"
                cssClassName="bg-white"
              />
            </div>
            <div className="w-64">
              <MultiDropdown
                name="categoryIds"
                value={formValues.categoryIds}
                options={categoryOptions}
                onChange={handleCategoryChange}
                placeholder={t("admin.user.permission.search.category.placeholder")}

              />
            </div>
            <div className="w-48">
              <Dropdown
                name="assignmentFilter"
                value={formValues.assignmentFilter}
                options={assignmentFilterOptions}
                onChange={handleDropdownChange}
                placeholder={t("admin.user.permission.search.filter.all")}
                cssClassName="bg-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <CheckBox
                checked={formValues.includeSubcategories}
                onChange={handleIncludeSubcategoriesChange}
              />
              <span className="text-sm text-gray-700">
                {t("admin.user.permission.search.includesubcategories")}
              </span>
            </div>
            <div className="flex items-center">
              <span
                onClick={handleResetFormClick}
                className="text-xs font-bold cursor-pointer text-gray-700"
              >
                {t("admin.reset")}
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="primary"
              children={`${t("admin.user.permission.search.edit")} (${selectedCount})`}
              startIcon="/media/icons/edit.svg"
              onClick={onBulkEdit}
              disabled={!selectedCount}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default memo(UserPermissionSearchFilters);
